import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { answer as saveAnswer, assign, getStatus, submit, subscribe, verifyEmail, } from "./api";
const EMPTY_ANSWERS = {
    personal: null,
    publicRec: null,
    dependent: null,
    expected: null,
    confidence: null,
    reason: "",
};
const CHOICE_QUESTIONS = [
    {
        key: "personal",
        numeral: "I",
        meta: "Question 01 — Personal",
        question: "Which button would you, personally, press?",
        apiField: "personalChoiceDisplayed",
    },
    {
        key: "publicRec",
        numeral: "II",
        meta: "Question 02 — Prescription",
        question: "Which button would you publicly recommend?",
        apiField: "publicRecommendationDisplayed",
    },
    {
        key: "dependent",
        numeral: "III",
        meta: "Question 03 — A child you love",
        question: "Which button would you tell your child or dependent to press?",
        apiField: "dependentRecommendationDisplayed",
    },
    {
        key: "expected",
        numeral: "IV",
        meta: "Question 04 — Prediction",
        question: "Which button do you think most people will press?",
        apiField: "expectedMajorityDisplayed",
    },
];
const TOTAL_STAGES = CHOICE_QUESTIONS.length + 1;
const FINAL_NUMERAL = "V";
const LOCK_HOLD_MS = 720;
/* ---------------------------------------------------------------------- */
/*  Component                                                             */
/* ---------------------------------------------------------------------- */
export function Poll() {
    const [phase, setPhase] = useState({ kind: "loading" });
    const [answers, setAnswers] = useState(EMPTY_ANSWERS);
    const [submitting, setSubmitting] = useState(false);
    const [closesAt, setClosesAt] = useState(null);
    const [totalResponses, setTotalResponses] = useState(null);
    const [lockedChoice, setLockedChoice] = useState(null);
    const advanceTimer = useRef(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const status = await getStatus();
                if (cancelled)
                    return;
                setClosesAt(status.closesAt);
                setTotalResponses(status.totalResponses);
                const a = await assign(captureReferrerFromUrl());
                if (cancelled)
                    return;
                if (a.alreadyVoted) {
                    setPhase({
                        kind: "done",
                        // Server now returns responseToken in alreadyVoted when the
                        // assignment cookie is still intact, so users who refreshed
                        // can still late-bind an email via the SubscribeForm.
                        token: a.responseToken ?? null,
                        shareCode: a.shareCode ?? null,
                        verified: a.verified ?? false,
                        email: a.email ?? null,
                    });
                    return;
                }
                // Resume from any saved partial state. The 4 choice questions are
                // ordered, so the first stage with a null answer is where they left
                // off; if all 4 are answered we drop them on the FinalStage.
                if (a.partial) {
                    setAnswers({
                        personal: a.partial.personalChoiceDisplayed,
                        publicRec: a.partial.publicRecommendationDisplayed,
                        dependent: a.partial.dependentRecommendationDisplayed,
                        expected: a.partial.expectedMajorityDisplayed,
                        confidence: a.partial.confidence,
                        reason: a.partial.reasonText ?? "",
                    });
                }
                const stage = firstUnansweredStage(a.partial);
                setPhase({
                    kind: "running",
                    token: a.responseToken,
                    condition: a.condition,
                    stage,
                });
            }
            catch (e) {
                if (!cancelled) {
                    const message = e.message;
                    setPhase(/poll is closed/i.test(message) ? { kind: "closed" } : { kind: "error", message });
                }
            }
        })();
        return () => {
            cancelled = true;
            if (advanceTimer.current !== null)
                window.clearTimeout(advanceTimer.current);
        };
    }, []);
    /**
     * Maps the saved partial answers (if any) onto the wizard's stage cursor.
     * Returns the index of the first unanswered choice question, or
     * CHOICE_QUESTIONS.length (the FinalStage) when all four are answered.
     */
    function firstUnansweredStage(p) {
        if (!p)
            return 0;
        const order = [
            p.personalChoiceDisplayed,
            p.publicRecommendationDisplayed,
            p.dependentRecommendationDisplayed,
            p.expectedMajorityDisplayed,
        ];
        const idx = order.findIndex((v) => v == null);
        return idx < 0 ? CHOICE_QUESTIONS.length : idx;
    }
    function handleChoice(displayedLabel) {
        if (phase.kind !== "running" || lockedChoice !== null)
            return;
        const stage = phase.stage;
        const q = CHOICE_QUESTIONS[stage];
        if (!q)
            return;
        setLockedChoice(displayedLabel);
        setAnswers((a) => ({ ...a, [q.key]: displayedLabel }));
        void saveAnswer({
            responseToken: phase.token,
            [q.apiField]: displayedLabel,
        }).catch((err) => {
            setPhase({ kind: "error", message: err.message });
        });
        advanceTimer.current = window.setTimeout(() => {
            advanceTimer.current = null;
            setLockedChoice(null);
            setPhase((p) => (p.kind === "running" ? { ...p, stage: p.stage + 1 } : p));
        }, LOCK_HOLD_MS);
    }
    function handleConfidence(n) {
        if (phase.kind !== "running")
            return;
        setAnswers((a) => ({ ...a, confidence: n }));
        void saveAnswer({ responseToken: phase.token, confidence: n }).catch((err) => {
            setPhase({ kind: "error", message: err.message });
        });
    }
    function handleReason(text) {
        setAnswers((a) => ({ ...a, reason: text }));
    }
    async function handleSubmit() {
        if (phase.kind !== "running" || submitting)
            return;
        if (answers.personal === null ||
            answers.publicRec === null ||
            answers.dependent === null ||
            answers.expected === null ||
            answers.confidence === null) {
            return;
        }
        setSubmitting(true);
        try {
            const res = await submit({
                responseToken: phase.token,
                reasonText: answers.reason.trim() || undefined,
            });
            setTotalResponses(res.totalResponses);
            // Fresh submit — the user is by definition not yet email-verified
            // (they haven't even seen the SubscribeForm). Server-reported state
            // takes over once they refresh.
            setPhase({
                kind: "done",
                token: phase.token,
                shareCode: res.shareCode ?? null,
                verified: false,
                email: null,
            });
        }
        catch (e) {
            setPhase({ kind: "error", message: e.message });
        }
        finally {
            setSubmitting(false);
        }
    }
    /* -------------- Phase rendering -------------- */
    if (phase.kind === "loading")
        return _jsx(Shell, { closesAt: closesAt, children: _jsx(Loading, {}) });
    if (phase.kind === "closed")
        return _jsx(Shell, { closesAt: closesAt, children: _jsx(Closed, {}) });
    if (phase.kind === "done")
        return (_jsx(Shell, { closesAt: closesAt, children: _jsx(Done, { totalResponses: totalResponses, token: phase.token, shareCode: phase.shareCode, serverVerified: phase.verified, serverEmail: phase.email }) }));
    if (phase.kind === "error")
        return (_jsx(Shell, { closesAt: closesAt, children: _jsx(ErrorView, { message: phase.message }) }));
    const stage = phase.stage;
    const condition = phase.condition;
    return (_jsx(Shell, { closesAt: closesAt, stage: stage, children: stage < CHOICE_QUESTIONS.length ? (_jsx(ChoiceStage, { stage: stage, condition: condition, locked: lockedChoice, onChoose: handleChoice }, stage)) : (_jsx(FinalStage, { confidence: answers.confidence, reason: answers.reason, submitting: submitting, onConfidence: handleConfidence, onReason: handleReason, onSubmit: handleSubmit, condition: condition }, stage)) }));
}
/* ---------------------------------------------------------------------- */
/*  Shell — left study rail + center main + right progress panel          */
/* ---------------------------------------------------------------------- */
function Shell({ children, stage, closesAt, }) {
    return (_jsxs("div", { className: "shell", children: [_jsxs("aside", { className: "study-rail", "aria-label": "Study information", children: [_jsxs("div", { className: "rail-block", children: [_jsx("span", { className: "rail-label", children: "Study" }), _jsx("span", { className: "rail-title", children: "Threshold" })] }), _jsxs("div", { className: "rail-block", children: [_jsx("span", { className: "rail-label", children: "Format" }), _jsx("span", { children: "Anonymous survey" })] }), _jsxs("div", { className: "rail-block", children: [_jsx("span", { className: "rail-label", children: "Response" }), _jsx("span", { children: "Single submission" })] }), closesAt && _jsx(Countdown, { closesAt: closesAt }), _jsxs("div", { className: "rail-block rail-date", children: [_jsx("span", { className: "rail-label", children: "Date" }), _jsx("span", { children: dateStamp() })] }), _jsx("a", { className: "rail-results-link", href: "/results", children: "Already responded? View results \u2192" })] }), _jsx("main", { className: "study-main", children: children }), _jsx("aside", { className: "progress-panel", "aria-label": "Survey progress", children: typeof stage === "number" ? (_jsx(ProgressPanel, { stage: stage })) : (_jsx("div", { className: "progress-empty", children: "Threshold Study" })) })] }));
}
function Countdown({ closesAt }) {
    const [remaining, setRemaining] = useState(() => closesAt && deltaUntil(closesAt));
    useEffect(() => {
        setRemaining(deltaUntil(closesAt));
        const id = window.setInterval(() => setRemaining(deltaUntil(closesAt)), 30_000);
        return () => window.clearInterval(id);
    }, [closesAt]);
    return (_jsxs("div", { className: "rail-block rail-countdown", children: [_jsx("span", { className: "rail-label", children: "Closes in" }), _jsx("span", { className: "countdown-value", children: remaining || "Closing..." })] }));
}
function ProgressPanel({ stage }) {
    const current = Math.min(stage + 1, TOTAL_STAGES);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "progress-kicker", children: "Progress" }), _jsxs("div", { className: "progress-count", children: [_jsx("span", { children: current }), _jsxs("small", { children: ["/ ", TOTAL_STAGES] })] }), _jsx("div", { className: "progress-track", "aria-hidden": true, children: _jsx("span", { style: { height: `${(current / TOTAL_STAGES) * 100}%` } }) })] }));
}
/* ---------------------------------------------------------------------- */
/*  Stages                                                                */
/* ---------------------------------------------------------------------- */
function ChoiceStage({ stage, condition, locked, onChoose, }) {
    const q = CHOICE_QUESTIONS[stage];
    const isFirst = stage === 0;
    // Button position is randomized per session (via the displayed label hash,
    // which varies across labelConditions) but stable across all questions in
    // the same session. Re-ordering between questions added cognitive load with
    // no experimental benefit — the per-session randomization happens once.
    const orderedLabels = useStableShuffle([condition.displayedThresholdLabel, condition.displayedSafeLabel], condition.displayedThresholdLabel);
    return (_jsxs("section", { className: "stage", children: [_jsxs("div", { className: "question-head", children: [_jsx("span", { className: "numeral", children: q.numeral }), _jsxs("div", { children: [_jsx("div", { className: "q-meta", children: q.meta }), _jsx("h1", { className: "q-text", children: q.question })] })] }), isFirst && _jsx(PromptBlock, { condition: condition }), _jsx("div", { className: "choices", children: orderedLabels.map((label) => (_jsx(ChoiceCard, { label: label, isLocked: locked === label, isDimmed: locked !== null && locked !== label, disabled: locked !== null, onClick: () => onChoose(label) }, label))) }), !isFirst && _jsx(RecallScenario, { condition: condition })] }));
}
function ChoiceCard({ label, isLocked, isDimmed, disabled, onClick, }) {
    const classes = ["choice", isLocked ? "locked" : "", isDimmed ? "dim" : ""]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("button", { type: "button", className: classes, onClick: onClick, disabled: disabled, children: [_jsx("span", { className: "seal", "aria-hidden": true }), _jsx("span", { className: "label", children: capitalize(label) }), _jsx("span", { className: "tag", children: "Press to commit" }), _jsx("span", { className: "underline", "aria-hidden": true })] }));
}
function FinalStage({ confidence, reason, submitting, onConfidence, onReason, onSubmit, condition, }) {
    return (_jsxs("section", { className: "stage", children: [_jsxs("div", { className: "question-head", children: [_jsx("span", { className: "numeral", children: FINAL_NUMERAL }), _jsxs("div", { children: [_jsx("div", { className: "q-meta", children: "Question 05 \u2014 Reflection" }), _jsx("h1", { className: "q-text", children: "How confident are you in your personal choice?" })] })] }), _jsxs("div", { className: "scale-legend", children: [_jsx("span", { children: "Not confident" }), _jsx("span", { children: "Extremely confident" })] }), _jsx("div", { className: "scale", role: "radiogroup", "aria-label": "Confidence", children: [1, 2, 3, 4, 5].map((n) => (_jsx("button", { type: "button", role: "radio", "aria-checked": confidence === n, className: confidence === n ? "selected" : "", onClick: () => onConfidence(n), children: n }, n))) }), _jsx("span", { className: "field-label", children: "Why did you choose that button? (optional)" }), _jsx("textarea", { className: "reason", value: reason, onChange: (e) => onReason(e.target.value), maxLength: 500, placeholder: "A sentence is plenty." }), _jsxs("div", { className: "counter", children: [reason.length, " / 500"] }), _jsxs("div", { className: "submit-row", children: [_jsx("button", { type: "button", className: "submit", disabled: confidence === null || submitting, onClick: onSubmit, children: submitting ? "Recording…" : "Submit response" }), _jsx("span", { className: "submit-note", children: "Once submitted, you cannot revise." })] }), _jsx(RecallScenario, { condition: condition })] }));
}
function Done({ totalResponses, token, shareCode, serverVerified, serverEmail, }) {
    // Initialize step from the server-reported state. Three cases:
    //   - row.email_verified_at set       → "verified" (suppress all email UI)
    //   - row.email set, not yet verified  → "verifying" (skip email entry,
    //                                        jump straight to OTC collection)
    //   - no email on file                 → "share" (full SubscribeForm)
    // Local transitions take over after first interaction.
    const [step, setStep] = useState(() => {
        if (serverVerified && serverEmail) {
            return { kind: "verified", email: serverEmail };
        }
        if (serverEmail) {
            return { kind: "verifying", email: serverEmail };
        }
        return { kind: "share" };
    });
    const [copied, setCopied] = useState(false);
    // Pulses the email/code form when the user hovers/clicks a locked cohort
    // card. Connects the carousel's "verify to unlock" call to the actual UI
    // that performs the unlock.
    const [unlockPulse, setUnlockPulse] = useState("idle");
    // Highlight visible email inputs while the user hovers/focuses/clicks
    // a locked card. No scroll, no focus shift, no animation — just a
    // static color/border change so the eye sees "these two are linked."
    // The click state holds longer (~900ms) than hover so a single click
    // still reads as a confirmation gesture before fading.
    function pulseFromCohort(kind) {
        setUnlockPulse(kind);
        if (kind === "click") {
            window.setTimeout(() => setUnlockPulse("idle"), 900);
        }
    }
    function clearPulse() {
        setUnlockPulse((p) => (p === "hover" ? "idle" : p));
    }
    // When we know the respondent's share_code, append it as ?ref=<code> so
    // anyone who answers through this link is attributed to them in the cohort
    // tree. Without a code, fall back to the bare site URL.
    const shareUrl = typeof window === "undefined"
        ? ""
        : shareCode
            ? `${window.location.origin}/?ref=${shareCode}`
            : `${window.location.origin}/`;
    // Tweet copy is intentionally neutral: no frame, no outcome, no color-loaded
    // wording. Just enough to make a friend curious enough to click through.
    const tweetText = "Two buttons. One choice. An anonymous coordination experiment — what would you press?";
    const tweetUrl = "https://twitter.com/intent/tweet?text=" +
        encodeURIComponent(tweetText) +
        "&url=" +
        encodeURIComponent(shareUrl);
    async function copy() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        }
        catch {
            /* clipboard unavailable */
        }
    }
    // Defensive: if the count is unknown OR somehow comes back below 1 while
    // we're already on the Done page (the row was wiped out from under us, etc.),
    // suppress the rank line entirely rather than render "1 of 0".
    const countLabel = totalResponses == null || totalResponses < 1
        ? null
        : totalResponses === 1
            ? "You're the first to answer."
            : `You're 1 of ${totalResponses.toLocaleString()} who have answered so far.`;
    return (_jsxs("section", { className: "coda done-share", children: [_jsx("div", { className: "done-kicker", children: "Recorded \u00B7 thank you" }), _jsx("h1", { children: "Done." }), countLabel && _jsx("p", { className: "done-rank", children: countLabel }), _jsxs("div", { className: `unlock-region${unlockPulse !== "idle" ? " unlock-region--" + unlockPulse : ""}`, children: [token && step.kind === "share" && (_jsx(SubscribeForm, { token: token, onSent: (email) => setStep({ kind: "verifying", email }) })), token && step.kind === "verifying" && (_jsx(VerifyForm, { token: token, email: step.email, onVerified: () => setStep({ kind: "verified", email: step.email }), onBack: () => setStep({ kind: "share" }) })), step.kind === "verified" && _jsx(VerifiedPanel, { email: step.email })] }), _jsx("div", { className: "rule" }), _jsxs("div", { className: "share-block", children: [_jsx("div", { className: "share-kicker", children: "Pass it on" }), _jsx("p", { className: "share-body", children: "The signal here is meaningful only when many people answer. Send it to a friend without spoiling the question." }), _jsxs("div", { className: "done-actions", children: [_jsx("a", { className: "share-twitter", href: tweetUrl, target: "_blank", rel: "noopener noreferrer", children: "Share on X" }), _jsx("button", { type: "button", className: "share-copy", onClick: copy, children: copied ? "Link copied" : "Copy link" })] }), _jsx("a", { className: "share-case-study", href: "/case-study", children: "Read the case study \u2192" })] }), shareCode && (_jsx(CohortPanel, { verified: step.kind === "verified", token: token, step: step, setStep: setStep, onCardEngage: pulseFromCohort, onCardLeave: clearPulse })), !token && (_jsx("p", { className: "done-footer", children: "Aggregate results publish when the poll closes." }))] }));
}
function SubscribeForm({ token, onSent, }) {
    const [email, setEmail] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    async function go(e) {
        e.preventDefault();
        setError(null);
        setPending(true);
        try {
            await subscribe({ responseToken: token, email: email.trim() });
            onSent(email.trim());
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setPending(false);
        }
    }
    return (_jsxs("div", { className: "subscribe", children: [_jsx("div", { className: "subscribe-kicker", children: "See how it lands" }), _jsx("h2", { className: "subscribe-headline", children: "Drop your email to see the result." }), _jsxs("p", { className: "subscribe-body", children: ["Two reasons. ", _jsx("strong", { children: "One:" }), " you'll see the live aggregate as soon as you confirm \u2014 a sneak peek before results publish.", _jsx("strong", { children: " Two:" }), " we'll email you once when the final results are in."] }), _jsxs("form", { className: "subscribe-form", onSubmit: go, children: [_jsx("input", { type: "email", autoComplete: "email", inputMode: "email", spellCheck: false, required: true, value: email, onChange: (e) => setEmail(e.target.value), placeholder: "you@example.com", "aria-label": "Email address" }), _jsx("button", { type: "submit", className: "submit", disabled: pending || !email.includes("@"), children: pending ? "Sending…" : "Send code" })] }), error && _jsx("p", { className: "error-text", children: error })] }));
}
function VerifyForm({ token, email, onVerified, onBack, }) {
    const [code, setCode] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    async function go(e) {
        e.preventDefault();
        setError(null);
        setPending(true);
        try {
            await verifyEmail({ responseToken: token, code });
            onVerified();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setPending(false);
        }
    }
    return (_jsxs("div", { className: "subscribe", children: [_jsx("div", { className: "subscribe-kicker", children: "Enter verification code" }), _jsx("h2", { className: "subscribe-headline", children: "Check your inbox." }), _jsxs("p", { className: "subscribe-body", children: ["We sent a 6-digit code to ", _jsx("strong", { children: email }), ". The code expires in 30 minutes."] }), _jsxs("form", { className: "otc-form", onSubmit: go, children: [_jsx("input", { type: "text", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, pattern: "\\d{6}", placeholder: "000000", value: code, onChange: (e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6)), "aria-label": "Verification code", autoFocus: true }), _jsx("button", { type: "submit", className: "submit", disabled: code.length !== 6 || pending, children: pending ? "Verifying…" : "Verify" }), _jsx("button", { type: "button", className: "share-copy", onClick: onBack, children: "Use a different email" })] }), error && _jsx("p", { className: "error-text", children: error })] }));
}
function VerifiedPanel({ email }) {
    return (_jsxs("div", { className: "subscribe verified", children: [_jsx("div", { className: "subscribe-kicker", children: "Verified \u2713" }), _jsx("h2", { className: "subscribe-headline", children: "Live preview unlocked." }), _jsxs("p", { className: "subscribe-body", children: ["Confirmed: ", _jsx("strong", { children: email }), ". You can now view the live aggregate and we'll email you when results are officially published."] }), _jsx("div", { className: "done-actions", children: _jsx("a", { className: "share-twitter", href: "/results", children: "View your results" }) })] }));
}
/**
 * Personal cohort teaser shown on the Done page.
 *
 * Rationale: the cohort viz lives on /results (single canonical "your view"
 * page). This panel exists only to (a) tell the user the feature exists,
 * (b) entice them with what they'll unlock by sharing/verifying, and
 * (c) link to /results when they're ready.
 *
 * Two states:
 *  - unverified: "View your results" is greyed; the silhouette is dimmer
 *    and the prompt asks them to verify above.
 *  - verified: link is live; preview is at full "locked" intensity.
 */
function CohortPanel({ verified, token, step, setStep, onCardEngage, onCardLeave, }) {
    // The inline form is a *second* entry point to the same subscribe flow
    // that lives at the top of the Done page. Both forms write to the same
    // parent step state, so they advance together.
    // Show the inline subscribe form right away. The cohort panel is the
    // user's second-look-and-decide moment; not making them click an extra
    // button to even see the email field.
    const [inlineOpen, setInlineOpen] = useState(true);
    // Auto-open when the parent moves the user into verifying/verified state
    // — if they used the top form, the inline panel should reflect that.
    useEffect(() => {
        if (step.kind !== "share")
            setInlineOpen(true);
    }, [step.kind]);
    return (_jsxs("div", { className: "cohort-panel", children: [_jsx("div", { className: "share-kicker", children: "Unlock your cohort" }), _jsxs("p", { className: "share-body", children: ["Share your link with three people to unlock ", _jsx("strong", { children: "your cohort" }), ". The four samples below are a preview \u2014 your full view will show your cohort's actual numbers, comparisons across depth, and how it diverges from the world."] }), _jsx(LockedCohortCarousel, { onCardEngage: onCardEngage, onCardLeave: onCardLeave }), _jsx("div", { className: "done-actions", children: _jsx("a", { className: "share-twitter", href: "/results", children: "View your results" }) }), !verified && inlineOpen && token && step.kind === "share" && (_jsx("div", { className: "cohort-inline-form unlock-region", children: _jsx(SubscribeForm, { token: token, onSent: (email) => setStep({ kind: "verifying", email }) }) })), !verified && inlineOpen && token && step.kind === "verifying" && (_jsx("div", { className: "cohort-inline-form unlock-region", children: _jsx(VerifyForm, { token: token, email: step.email, onVerified: () => setStep({ kind: "verified", email: step.email }), onBack: () => setStep({ kind: "share" }) }) }))] }));
}
/**
 * Locked cohort carousel — four sample-data viz cards in a horizontal
 * scroll, each with a frosted veil + "Verify to unlock" overlay. Replaces
 * the older silhouette preview. The cards always show sample data on the
 * Done page; the real data lives at /results once the user verifies.
 *
 * Each card's SVG is hand-rolled (no chart library) for byte-cost reasons:
 * the four shapes are static and total ~2 KB inline. The veil overlay is
 * rendered via CSS .locked-card .lock-veil so the lock state is purely a
 * styling concern and never mutates the underlying viz markup.
 */
function LockedCohortCarousel({ onCardEngage, onCardLeave, }) {
    // The cards live in a flex-row scroll lane; each card carries its own
    // hover/click handlers that bubble back to Done so the form pulses.
    const handlers = { onCardEngage, onCardLeave };
    return (_jsxs("div", { className: "locked-carousel", "aria-label": "Sample cohort visualizations", children: [_jsx(DriftCard, { ...handlers }), _jsx(BigNumberCard, { ...handlers }), _jsx(ConstellationCard, { ...handlers }), _jsx(RadarCard, { ...handlers })] }));
}
/* Each card has the same outer chrome: kicker, title, viz, then a frosted
   .lock-veil overlay anchored to the card's relative box. */
function LockedCard({ label, children, onCardEngage, onCardLeave, }) {
    return (_jsxs("button", { type: "button", className: "locked-card", onMouseEnter: () => onCardEngage("hover"), onMouseLeave: onCardLeave, onFocus: () => onCardEngage("hover"), onBlur: onCardLeave, onClick: () => onCardEngage("click"), children: [_jsx("span", { className: "sample-badge", "aria-hidden": "true", children: "Sample" }), _jsx("div", { className: "locked-card-label", children: label }), _jsx("div", { className: "locked-card-viz", "aria-hidden": "true", children: children })] }));
}
function DriftCard(props) {
    return (_jsx(LockedCard, { label: "Drift", ...props, children: _jsxs("svg", { viewBox: "0 0 320 130", preserveAspectRatio: "xMidYMid meet", role: "img", children: [_jsx("line", { x1: "0", y1: "78", x2: "320", y2: "78", stroke: "#d3cdbb", strokeDasharray: "3 3" }), _jsx("polyline", { points: "40,16 160,16 280,80", fill: "none", stroke: "#1f4886", strokeWidth: "2.5" }), _jsx("circle", { cx: "40", cy: "16", r: "5", fill: "#1f4886" }), _jsx("circle", { cx: "160", cy: "16", r: "5", fill: "#1f4886" }), _jsx("circle", { cx: "280", cy: "80", r: "5", fill: "#a8331e" })] }) }));
}
function BigNumberCard(props) {
    return (_jsxs(LockedCard, { label: "Big number", ...props, children: [_jsx("div", { className: "locked-bignum", children: "79%" }), _jsxs("div", { className: "locked-bignum-vs", children: [_jsx("strong", { children: "\u2191 7 pp" }), " more cooperative than world"] }), _jsxs("div", { className: "locked-bignum-strip", children: [_jsx("span", { style: { width: "79%", background: "var(--communal)" } }), _jsx("span", { style: { width: "21%", background: "var(--selfish)" } })] })] }));
}
function ConstellationCard(props) {
    return (_jsx(LockedCard, { label: "Constellation", ...props, children: _jsxs("svg", { viewBox: "0 0 340 200", preserveAspectRatio: "xMidYMid meet", role: "img", children: [_jsx("circle", { cx: "170", cy: "100", r: "40", fill: "none", stroke: "#d3cdbb", strokeDasharray: "3 3" }), _jsx("circle", { cx: "170", cy: "100", r: "75", fill: "none", stroke: "#d3cdbb", strokeDasharray: "3 3" }), _jsx("line", { x1: "170", y1: "100", x2: "200", y2: "70", stroke: "#5b6055" }), _jsx("line", { x1: "170", y1: "100", x2: "140", y2: "70", stroke: "#5b6055" }), _jsx("line", { x1: "170", y1: "100", x2: "140", y2: "130", stroke: "#5b6055" }), _jsx("line", { x1: "170", y1: "100", x2: "200", y2: "130", stroke: "#5b6055" }), _jsx("line", { x1: "200", y1: "70", x2: "235", y2: "50", stroke: "#5b6055" }), _jsx("line", { x1: "200", y1: "70", x2: "245", y2: "95", stroke: "#5b6055" }), _jsx("line", { x1: "140", y1: "70", x2: "100", y2: "50", stroke: "#5b6055" }), _jsx("circle", { cx: "170", cy: "100", r: "9", fill: "#263d31" }), _jsx("circle", { cx: "200", cy: "70", r: "6", fill: "#1f4886" }), _jsx("circle", { cx: "140", cy: "70", r: "6", fill: "#1f4886" }), _jsx("circle", { cx: "140", cy: "130", r: "6", fill: "#1f4886" }), _jsx("circle", { cx: "200", cy: "130", r: "6", fill: "#a8331e" }), _jsx("circle", { cx: "235", cy: "50", r: "5", fill: "#1f4886" }), _jsx("circle", { cx: "245", cy: "95", r: "5", fill: "#a8331e" }), _jsx("circle", { cx: "100", cy: "50", r: "5", fill: "#1f4886" })] }) }));
}
function RadarCard(props) {
    return (_jsx(LockedCard, { label: "Radar", ...props, children: _jsxs("svg", { viewBox: "0 0 320 200", preserveAspectRatio: "xMidYMid meet", role: "img", children: [_jsxs("g", { stroke: "#d3cdbb", fill: "none", strokeDasharray: "2 3", children: [_jsx("circle", { cx: "160", cy: "110", r: "30" }), _jsx("circle", { cx: "160", cy: "110", r: "60" }), _jsx("circle", { cx: "160", cy: "110", r: "85" })] }), _jsx("line", { x1: "160", y1: "110", x2: "160", y2: "25", stroke: "#5b6055" }), _jsx("line", { x1: "160", y1: "110", x2: "245", y2: "110", stroke: "#5b6055" }), _jsx("line", { x1: "160", y1: "110", x2: "160", y2: "195", stroke: "#5b6055" }), _jsx("line", { x1: "160", y1: "110", x2: "75", y2: "110", stroke: "#5b6055" }), _jsx("polygon", { points: "160,65 220,110 160,125 130,110", fill: "#5b605533", stroke: "#5b6055", strokeDasharray: "3 3" }), _jsx("polygon", { points: "160,65 230,110 160,124 132,110", fill: "rgba(38,61,49,0.5)", stroke: "#263d31", strokeWidth: 2 })] }) }));
}
/* ---------------------------------------------------------------------- */
/*  Other end states                                                      */
/* ---------------------------------------------------------------------- */
function Loading() {
    return (_jsx("section", { className: "stage", children: _jsx("p", { className: "prompt", style: { fontStyle: "italic", color: "var(--muted)" }, children: "Setting the table\u2026" }) }));
}
function Closed() {
    return (_jsxs("section", { className: "coda", children: [_jsx("h1", { children: "The poll has closed." }), _jsx("div", { className: "rule" }), _jsx("p", { children: "Thank you for your interest. No further responses are being accepted." })] }));
}
function ErrorView({ message }) {
    return (_jsxs("section", { className: "coda", children: [_jsx("h1", { children: "Something went sideways." }), _jsx("div", { className: "rule" }), _jsx("p", { className: "error-text", children: message }), _jsx("p", { children: "Refreshing the page will start a new assignment." })] }));
}
/* ---------------------------------------------------------------------- */
/*  Prompt rendering                                                       */
/* ---------------------------------------------------------------------- */
function PromptBlock({ condition }) {
    if (condition.promptIsHtml) {
        return (_jsx("div", { className: "prompt", dangerouslySetInnerHTML: { __html: condition.promptText } }));
    }
    return (_jsx("div", { className: "prompt", children: _jsx("p", { children: condition.promptText }) }));
}
function RecallScenario({ condition }) {
    return (_jsxs("details", { className: "recall", children: [_jsx("summary", { children: "Recall the scenario" }), _jsx(PromptBlock, { condition: condition })] }));
}
/* ---------------------------------------------------------------------- */
/*  Helpers                                                               */
/* ---------------------------------------------------------------------- */
function capitalize(s) {
    if (!s)
        return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function dateStamp() {
    const d = new Date();
    return d
        .toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
        .toUpperCase();
}
function deltaUntil(iso) {
    const ms = new Date(iso).valueOf() - Date.now();
    if (!isFinite(ms) || ms <= 0)
        return "";
    const totalMin = Math.floor(ms / 60_000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin % (60 * 24)) / 60);
    const minutes = totalMin % 60;
    if (days > 0)
        return `${days}d ${hours}h`;
    if (hours > 0)
        return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}
function useStableShuffle(items, key) {
    let h = 2166136261;
    for (let i = 0; i < key.length; i++) {
        h ^= key.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    const r = (h >>> 0) / 0xffffffff;
    return r >= 0.5 ? [...items].reverse() : items;
}
/**
 * Read referrer / UTM params off the page URL on first load. After we read
 * them, we strip them from the URL via history.replaceState so that if the
 * user shares the tab URL it doesn't carry someone else's attribution.
 * The captured values are then included in the /api/poll/assign POST body
 * and persisted on the row via the signed token.
 */
function captureReferrerFromUrl() {
    if (typeof window === "undefined")
        return undefined;
    const params = new URLSearchParams(window.location.search);
    const fields = ["ref", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const data = {};
    let touched = false;
    for (const k of fields) {
        const v = params.get(k);
        if (v) {
            data[k] = v;
            params.delete(k);
            touched = true;
        }
    }
    if (touched) {
        const remaining = params.toString();
        const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : "") + window.location.hash;
        window.history.replaceState(null, "", cleanUrl);
        return data;
    }
    return undefined;
}
